import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faUsers, faQuestionCircle, faTimes, faEdit } from '@fortawesome/free-solid-svg-icons';
import productService from '../../../services/productService';

// Estilos para o modal de detalhes do produto
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  background: rgba(20, 30, 48, 0.9);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 2rem;
  border: 1px solid rgba(100, 182, 255, 0.2);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(-50px)'};
  transition: all 0.3s ease;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 182, 255, 0.5);
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #fff;
    transform: scale(1.1);
  }
`;

const ProductTitle = styled.h2`
  color: #fff;
  font-size: 1.8rem;
  margin: 0 0 1.5rem 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(100, 182, 255, 0.2);
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: #64B6FF;
  font-size: 1.2rem;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.8rem;
  padding: 0.8rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  
  svg {
    color: #64B6FF;
    font-size: 1.2rem;
    min-width: 20px;
  }
`;

const DetailLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  min-width: 120px;
`;

const DetailValue = styled.span`
  color: #fff;
  font-weight: 500;
`;

const QuestionItem = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 3px solid #64B6FF;
`;

const FAQItem = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  
  h4 {
    color: #64B6FF;
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    font-size: 0.9rem;
  }
`;

const EditButton = styled.button`
  background: linear-gradient(135deg, #4A00E0, #8E2DE2);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(142, 45, 226, 0.4);
  }
`;

const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(to right, rgba(100, 182, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(100, 182, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  z-index: -1;
`;

// Componente principal
const ProductDetails = ({ productId, show, onClose, onEdit }) => {
  const [product, setProduct] = useState(null);
  
  // Carregar dados do produto
  useEffect(() => {
    if (productId && show) {
      const productData = productService.getProductById(productId);
      setProduct(productData);
    }
  }, [productId, show]);
  
  // Fechar o modal ao pressionar a tecla ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && show) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);
  
  // Impedir a rolagem do body quando o modal estiver aberto
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [show]);
  
  if (!product) return null;
  
  return (
    <ModalOverlay show={show} onClick={onClose}>
      <ModalContent show={show} onClick={e => e.stopPropagation()}>
        <TechGrid />
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        
        <ProductTitle>{product.productName}</ProductTitle>
        
        <DetailSection>
          <SectionTitle>
            <FontAwesomeIcon icon={faCalendarAlt} /> Informações Básicas
          </SectionTitle>
          
          <DetailItem>
            <FontAwesomeIcon icon={faCalendarAlt} />
            <DetailLabel>Tipo de Agendamento:</DetailLabel>
            <DetailValue>{product.appointmentType}</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <FontAwesomeIcon icon={faClock} />
            <DetailLabel>Duração:</DetailLabel>
            <DetailValue>{product.duration} minutos</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <FontAwesomeIcon icon={faUsers} />
            <DetailLabel>Capacidade:</DetailLabel>
            <DetailValue>{product.capacity} {product.capacity > 1 ? 'pessoas' : 'pessoa'}</DetailValue>
          </DetailItem>
        </DetailSection>
        
        {product.qualifyingQuestions && product.qualifyingQuestions.length > 0 && (
          <DetailSection>
            <SectionTitle>
              <FontAwesomeIcon icon={faQuestionCircle} /> Perguntas Qualificatórias
            </SectionTitle>
            
            {product.qualifyingQuestions.map((question, index) => (
              <QuestionItem key={question.id || index}>
                <p>{question.question}</p>
              </QuestionItem>
            ))}
          </DetailSection>
        )}
        
        {product.faqs && product.faqs.length > 0 && (
          <DetailSection>
            <SectionTitle>
              <FontAwesomeIcon icon={faQuestionCircle} /> Perguntas Frequentes
            </SectionTitle>
            
            {product.faqs.map((faq, index) => (
              <FAQItem key={faq.id || index}>
                <h4>{faq.question}</h4>
                <p>{faq.answer}</p>
              </FAQItem>
            ))}
          </DetailSection>
        )}
        
        <EditButton onClick={() => onEdit(productId)}>
          <FontAwesomeIcon icon={faEdit} /> Editar Produto
        </EditButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProductDetails;
